import { useState } from 'react';
import { X, Search, ArrowUpDown, ChevronDown } from 'lucide-react';
import taricData from '../../../taric_tables_data.json';

export function getAduanas(dataSource = 'aduanas-espanolas') {
  // Handle array of data sources
  if (Array.isArray(dataSource)) {
    const allAduanas = [];
    dataSource.forEach((source) => {
      const aduanasFromSource = (taricData[source] || []).map((aduana) => ({
        nombre: aduana.descripcion,
        codigo: aduana.codigo,
      }));
      allAduanas.push(...aduanasFromSource);
    });
    return allAduanas;
  }
  
  // Handle single string data source
  return (taricData[dataSource] || []).map((aduana) => ({
    nombre: aduana.descripcion,
    codigo: aduana.codigo,
  }));
}

export const AduanaModal = ({
  aduanas,
  isOpen,
  onClose,
  onSelect,
  selectedAduana,
  title = 'Select Customs Office',
  dataSource = 'aduanas-espanolas',
}) => {
  // Use provided aduanas or fetch from dataSource
  const modalAduanas = aduanas || getAduanas(dataSource);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [isSortOpen, setIsSortOpen] = useState(false);

  const sortOptions = [
    { value: 'name-asc', label: 'Name (A-Z)' },
    { value: 'name-desc', label: 'Name (Z-A)' },
  ];

  const filteredAduanas = modalAduanas.filter((aduana) => {
    const query = searchQuery.toLowerCase();
    const matchesNombre = aduana.nombre?.toLowerCase().includes(query);
    const matchesCodigo = aduana.codigo?.toLowerCase().includes(query);
    return matchesNombre || matchesCodigo;
  });

  const sortedAduanas = [...filteredAduanas].sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return a.nombre.localeCompare(b.nombre);
      case 'name-desc':
        return b.nombre.localeCompare(a.nombre);
      default:
        return 0;
    }
  });

  const getSortLabel = () => {
    return sortOptions.find((option) => option.value === sortBy)?.label || 'Sort by';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button
            onClick={() => {
              setSearchQuery('');
              setIsSortOpen(false);
              onClose();
            }}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          {/* Search Input */}
          <div className="relative flex items-center gap-2 border-container rounded-full px-3 py-1 flex-1 flex-row">
            <div className="flex-0 pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="pr-4 py-2 !border-none no-border search-input"
              style={{ borderWidth: '0px !important', border: '0px !important' }}
              placeholder="Search customs offices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Custom Sort Dropdown */}
          <div className="relative button-no-hover-effect text-sm">
            <button
              className="flex items-center gap-2 px-2 pr-0 py-2 rounded-full"
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <ArrowUpDown className="w-4 h-4" />
              <span>{getSortLabel()}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
            </button>

            {isSortOpen && (
              <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      sortBy === option.value ? 'text-primary' : 'text-gray-700'
                    }`}
                    onClick={() => {
                      setSortBy(option.value);
                      setIsSortOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="max-h-[60vh] min-h-[60vh] overflow-y-auto">
          {modalAduanas.length > 0 ? (
            <div className="space-y-0">
              {sortedAduanas.length > 0 ? (
                sortedAduanas.map((aduana) => (
                  <div
                    key={aduana.codigo || aduana.nombre}
                    className={`p-3 px-0 hover:bg-gray-100 cursor-pointer border-bottom-gray ${
                      selectedAduana &&
                      (selectedAduana.codigo === aduana.codigo || selectedAduana.nombre === aduana.nombre)
                        ? 'bg-gray-100 font-bold'
                        : ''
                    }`}
                    onClick={() => {
                      onSelect(aduana);
                      setSearchQuery('');
                      setIsSortOpen(false);
                      onClose();
                    }}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <p className="font-medium">{aduana.nombre}</p>
                        {aduana.codigo && (
                          <p className="text-xs text-gray-500">{aduana.codigo}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  No customs offices found matching "{searchQuery}"
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className='opacity-75 mb-4'>No Customs Offices Available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 